/**
 * Portals, as the substrate reports them.
 *
 * The registry lives in the database and is served by the backend, so every
 * Portal agrees on where the others live and DNS can move without rebuilding
 * N frontends. Nothing about Portal locations is compiled into this file.
 */

import { api } from "./api";

export const CROSS_PARAM = "cross";

let cache = null;

export const loadPortals = async () => {
  if (cache) return cache;
  const { data } = await api.get("/portals");
  cache = data;
  return cache;
};

export const here = () =>
  typeof window === "undefined" ? "" : window.location.origin;

/**
 * Suspender map: when the DB registry says a Portal's origin is blank (usually
 * because that Portal's PORTAL_ORIGIN_* env var wasn't set in production yet),
 * the browser's own hostname is still the truth. Landing on endeavorum.org
 * MUST resolve to the Commons whether the backend told us or not — otherwise
 * the visitor sees the .com Atlas at a .org URL, which is the "Ghost World is
 * showing on the wrong front door" symptom.
 *
 * Keyed by bare hostname (no scheme, no www). Only holds first-party front
 * doors — third-party Portals will always have a registry row and never fall
 * through to this map.
 */
const HOSTNAME_TO_PORTAL = {
  "endeavorum.com": "endeavorum",
  "endeavorum.org": "commons",
  "playforward.app": "playforward",
  "stageward.app": "stageward",
};

/** Which Portal is this browser sitting in? Host, not route. */
export const currentPortal = (list) => {
  // ?portal= lets a Practice door be inspected from the preview host, where the
  // hostname can only ever say "endeavorum". Visual only — a session still
  // belongs to the origin it was minted on.
  const forced =
    typeof window === "undefined"
      ? null
      : new URLSearchParams(window.location.search).get("portal");
  if (forced && (list || []).some((p) => p.key === forced)) return forced;

  // `www.` is stripped on both sides before matching. A host that serves the
  // www form (or a registrar/CDN that redirects the apex to it) would otherwise
  // miss every registered origin and silently fall back to the world door —
  // the visitor lands on the Atlas while the URL bar says playforward.app.
  const bare = (s) => (s || "").toLowerCase().replace("://www.", "://");
  const origin = bare(here());
  const byOrigin = (list || []).find((p) => p.origin && bare(p.origin) === origin);
  if (byOrigin) return byOrigin.key;
  const host = (
    typeof window === "undefined" ? "" : window.location.hostname
  )
    .toLowerCase()
    .replace(/^www\./, "");
  const byHost = (list || []).find(
    (p) => host === `${p.key}.app` || host.startsWith(`${p.key}.`)
  );
  if (byHost) return byHost.key;
  // Suspender: hardcoded first-party hostname map. Only reached when the
  // registry has no origin OR a Portal doesn't follow the "{key}.app" pattern
  // (Commons is on endeavorum.org, not commons.app).
  const suspender = HOSTNAME_TO_PORTAL[host];
  if (suspender && (list || []).some((p) => p.key === suspender)) {
    return suspender;
  }
  return "endeavorum";
};
